/**
 * Logger Utility
 * 
 * Provides environment-aware logging that:
 * - Shows logs in development
 * - Suppresses logs in production
 * - Can integrate with error tracking services (Sentry, LogRocket, etc.)
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Environment-aware logger
 */
export const logger = {
  /**
   * Log informational messages (development only)
   */
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Log errors
   * In development: logs to console
   * In production: silent (integrate with error tracking service)
   */
  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error(...args);
    } else if (isProduction) {
      // TODO: Send to error tracking service
      // Example with Sentry:
      // if (args[0] instanceof Error) {
      //   Sentry.captureException(args[0]);
      // } else {
      //   Sentry.captureMessage(String(args[0]), 'error');
      // }
    }
  },

  /**
   * Log warnings (development only)
   */
  warn: (...args: any[]) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Log API calls for debugging (development only)
   */
  api: (url: string, method: string = 'GET', data?: any) => {
    if (isDevelopment) {
      console.log(`[API ${method}]`, url, data ? { data } : '');
    }
  },

  /**
   * Group logs together (development only)
   */
  group: (label: string, callback: () => void) => {
    if (isDevelopment) {
      console.group(label);
      callback();
      console.groupEnd();
    }
  },

  /**
   * Log performance metrics (development only)
   */
  perf: (label: string, startTime: number) => {
    if (isDevelopment) {
      const duration = performance.now() - startTime;
      console.log(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
    }
  }
};

/**
 * Usage Examples:
 * 
 * // Replace console.log
 * logger.log('User data loaded:', userData);
 * 
 * // Replace console.error
 * logger.error('Failed to fetch data:', error);
 * 
 * // API calls
 * logger.api('/api/players', 'GET');
 * 
 * // Grouped logs
 * logger.group('Player Data', () => {
 *   logger.log('Players:', players);
 *   logger.log('Count:', players.length);
 * });
 * 
 * // Performance tracking
 * const start = performance.now();
 * // ... some operation
 * logger.perf('Load Players', start);
 */

export default logger;
